import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  fetchAbout,
  fetchContact,
  fetchFAQ,
  fetchHeroBanners,
  fetchReturnPolicy,
  fetchThemeSettings,
  fetchPaymentConfig,
  fetchSettings,
  fetchSocialLinks,
  fetchTeam,
  fetchServices,
  fetchTestimonials
} from '../utils/api';

const ContentContext = createContext();

export function ContentProvider({ children }) {
  const [content, setContent] = useState({
    about: null,
    contact: null,
    faq: null,
    returnPolicy: null,
    heroBanner: null,
    themeSettings: null,
    paymentConfig: null,
    settings: null,
    socialLinks: null,
    team: null,
    testimonials: null,
    services: null
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      const promises = [
        { key: 'about', fetch: fetchAbout },
        { key: 'contact', fetch: fetchContact },
        { key: 'faq', fetch: fetchFAQ },
        { key: 'heroBanner', fetch: fetchHeroBanners },
        { key: 'returnPolicy', fetch: fetchReturnPolicy },
        { key: 'themeSettings', fetch: fetchThemeSettings },
        { key: 'paymentConfig', fetch: fetchPaymentConfig },
        { key: 'settings', fetch: fetchSettings },
        { key: 'socialLinks', fetch: fetchSocialLinks },
        { key: 'team', fetch: fetchTeam },
        { key: 'testimonials', fetch: fetchTestimonials },
        { key: 'services', fetch: fetchServices },
      ];

      const results = await Promise.allSettled(promises.map(({ fetch }) => fetch()));

      const newContent = { ...content };
      const newErrors = {};

      results.forEach((result, index) => {
        const { key } = promises[index];
        if (result.status === 'fulfilled' && result.value) {
          newContent[key] = result.value;
        } else if (result.status === 'rejected') {
          newErrors[key] = `Failed to load ${key}`;
          console.error(`Content fetch error for ${key}:`, result.reason);
        } else {
          newErrors[key] = `Empty response for ${key}`;
          console.warn(`Content fetch warning for ${key}: Empty response`);
        }
      });

      setContent(newContent);
      setErrors(newErrors);
      setLoading(false);
    };

    fetchContent();
  }, []);

  return (
    <ContentContext.Provider value={{ content, errors, loading }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
}